import { FullLogo } from '@/components/ap/logos/Logos';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  SidebarTrigger,
} from '@/components/ui/sidebar';
import { useEffectiveDirection } from '@/lib/hooks/useEffectiveDirection';
import { AltArrowDownLinear } from '@assessprep/icons/solar';
import { CanAccess, ITreeMenu, useMenu } from '@refinedev/core';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';
// Pre-existing boundary violation inherited from dev, not introduced here: a
// components/ap file may not import from pages. The real fix is for the
// composing page to pass IcpText in; annotated so merges are not blocked.
// eslint-disable-next-line no-restricted-imports
import { IcpText } from '@/pages/auth/ICPText';
import { SidebarSupport } from './SidebarSupport';
import { UserMenu } from './UserMenu';

/**
 * APL-11850 (Slice 5, rebuilt fix round J) — v4 sidebar on shadcn's OFFICIAL
 * Sidebar family (ui/sidebar.tsx), replacing the hand-rolled aside per
 * .neo/rules/frontend.md § Stock-first gate. Driven by the SAME shared Refine
 * `resources` list (src/app/resources.tsx) through `useMenu()`, with the SAME
 * per-item CanAccess gating the v1 ThemedSiderV2 render had (v1 shell
 * deleted at R15b-b3). `useMenu()` already filters `options.hide` resources.
 *
 * Collapse model: `collapsible="icon"` — SidebarTrigger in the header (and
 * Cmd/Ctrl+B, and the SidebarRail edge) collapses to an icon rail; menu
 * buttons then surface their labels as tooltips. On mobile (<768px) the
 * family renders a portaled Sheet instead.
 *
 * Resource icons come from the shared resources config (icon ELEMENTS in
 * meta.icon) — rendered, not imported, so the v4 import bans hold while the
 * menus stay visually identical across shells. They arrive wrapped (not bare
 * `<svg>` children), hence the `[&_svg]` descendant sizing on the buttons.
 */

/** Descendant-svg sizing — resource icons render inside an aria-hidden span. */
const iconSizingClass = '[&_svg]:size-4 [&_svg]:shrink-0';

const ItemIcon = ({ icon }: { icon: ITreeMenu['icon'] }) =>
  icon ? (
    <span aria-hidden className="inline-flex shrink-0 items-center">
      {icon}
    </span>
  ) : null;

/** True when the selected menu item lives anywhere under this subtree. */
const containsSelected = (item: ITreeMenu, selectedKey?: string): boolean => {
  if (selectedKey === undefined) return false;
  if (item.key === selectedKey) return true;
  return item.children.some((child) => containsSelected(child, selectedKey));
};

// APL-12040: a resource may point OUT of the app (`meta.externalUrl`) — the
// student/parent Help entry opens the Zendesk For-Students category. NOT
// because react-router can't: RR 7's <Link> detects cross-origin absolute
// URLs (ABSOLUTE_URL_REGEX → isExternal), keeps the raw href and skips the
// SPA click handler — it would navigate fine. The real anchor exists for
// (a) target="_blank" + rel="noopener noreferrer": a Link navigates in-tab,
//     and `noreferrer` keeps in-app paths (assessment/experience IDs) out of
//     the Referer sent to the external site;
// (b) active-state opt-out: an external destination is never the "current
//     page", so isActive / aria-current are suppressed.
// Scheme allowlist: this is shared shell code for every authenticated user
// and React renders `javascript:` hrefs (it only console-warns), so anything
// but https: degrades to the internal <Link> branch.
const externalMenuUrl = (item: ITreeMenu): string | undefined => {
  const url = item.meta?.externalUrl;
  if (typeof url !== 'string') return undefined;
  try {
    return new URL(url).protocol === 'https:' ? url : undefined;
  } catch {
    return undefined;
  }
};

/**
 * The single anchor-vs-Link seam — used by BOTH MenuLeaf and MenuGroup's
 * child branch so `meta.externalUrl` means the same thing at every depth.
 * Returns a real element (not a component) so the `asChild` Slot merges the
 * surrounding button's props/ref straight onto the anchor/Link.
 */
const menuItemLink = (item: ITreeMenu, isSelected: boolean) => {
  const externalUrl = externalMenuUrl(item);
  return externalUrl ? (
    <a
      href={externalUrl}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`v4-nav-${item.name}`}
    >
      <ItemIcon icon={item.icon} />
      <span>{item.label}</span>
    </a>
  ) : (
    <Link
      to={item.route ?? ''}
      aria-current={isSelected ? 'page' : undefined}
      data-testid={`v4-nav-${item.name}`}
    >
      <ItemIcon icon={item.icon} />
      <span>{item.label}</span>
    </Link>
  );
};

const MenuLeaf = ({ item, selectedKey }: { item: ITreeMenu; selectedKey?: string }) => {
  const isSelected = item.key === selectedKey && !externalMenuUrl(item);
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={isSelected}
        tooltip={typeof item.label === 'string' ? item.label : undefined}
        className={iconSizingClass}
      >
        {menuItemLink(item, isSelected)}
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};

const MenuGroup = ({ item, selectedKey }: { item: ITreeMenu; selectedKey?: string }) => (
  <Collapsible
    asChild
    defaultOpen={containsSelected(item, selectedKey)}
    className="group/collapsible"
  >
    <SidebarMenuItem>
      <CollapsibleTrigger asChild>
        <SidebarMenuButton
          tooltip={typeof item.label === 'string' ? item.label : undefined}
          data-testid={`v4-nav-group-${item.name}`}
          className={iconSizingClass}
        >
          <ItemIcon icon={item.icon} />
          <span className="flex-1">{item.label}</span>
          <AltArrowDownLinear
            aria-hidden
            className="transition-transform group-data-[state=open]/collapsible:rotate-180"
          />
        </SidebarMenuButton>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <SidebarMenuSub>
          {item.children.map((child) => {
            const isChildSelected = child.key === selectedKey && !externalMenuUrl(child);
            return (
              <CanAccess
                key={child.key}
                resource={child.name}
                action={child.meta?.action ?? 'list'}
                params={{ resource: child }}
              >
                {child.children.length > 0 ? (
                  // Depth > 2 doesn't exist in resources.tsx today; render any
                  // future deep group as a nested collapsible at sub level.
                  <MenuGroup item={child} selectedKey={selectedKey} />
                ) : (
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton
                      asChild
                      isActive={isChildSelected}
                      className={iconSizingClass}
                    >
                      {menuItemLink(child, isChildSelected)}
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                )}
              </CanAccess>
            );
          })}
        </SidebarMenuSub>
      </CollapsibleContent>
    </SidebarMenuItem>
  </Collapsible>
);

export const AppSidebar = () => {
  const { t } = useTranslation();
  const { menuItems, selectedKey } = useMenu();
  const { pathname } = useLocation();
  // J21-B139: refine's selectedKey is an exact resource-route match, so deep
  // routes under an entry (/settings/programs/98/subjects) left the sidebar
  // unhighlighted. When the exact match misses, fall back to the menu item
  // whose route is the longest path-segment prefix of the current URL.
  const effectiveSelectedKey = useMemo(() => {
    const flat: { key: string; route: string }[] = [];
    const collect = (items: ITreeMenu[]) =>
      items.forEach((i) => {
        if (i.key && i.route) flat.push({ key: String(i.key), route: i.route });
        if (i.children.length > 0) collect(i.children);
      });
    collect(menuItems);
    if (flat.some((f) => f.key === selectedKey)) return selectedKey;
    const best = flat
      .filter(
        (f) => f.route !== '/' && (pathname === f.route || pathname.startsWith(`${f.route}/`))
      )
      .sort((a, b) => b.route.length - a.route.length)[0];
    return best?.key ?? selectedKey;
  }, [menuItems, selectedKey, pathname]);
  // shadcn Sidebar positions via a physical side prop — flip it under RTL.
  const side = useEffectiveDirection() === 'rtl' ? 'right' : 'left';

  return (
    <Sidebar side={side} collapsible="icon" data-testid="v4-sidebar">
      <SidebarHeader>
        <div className="flex h-10 items-center justify-between ps-2 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:ps-0">
          <Link
            to="/"
            aria-label={t('V4Shell.home')}
            className="group-data-[collapsible=icon]:hidden"
          >
            <FullLogo height={32} width={160} />
          </Link>
          <SidebarTrigger />
        </div>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <nav aria-label={t('V4Shell.main_navigation')}>
            <SidebarMenu>
              {menuItems.map((item) => (
                <CanAccess
                  key={item.key}
                  resource={item.name}
                  action={item.meta?.action ?? 'list'}
                  params={{ resource: item }}
                >
                  {item.children.length > 0 ? (
                    <MenuGroup item={item} selectedKey={effectiveSelectedKey} />
                  ) : (
                    <MenuLeaf item={item} selectedKey={effectiveSelectedKey} />
                  )}
                </CanAccess>
              ))}
            </SidebarMenu>
          </nav>
        </SidebarGroup>
      </SidebarContent>
      {/* Footer — v3 SiderFooter order: What's new + Help center (staff-only,
          SidebarSupport) above the user row (Display settings live in the
          avatar menu since the accessibility-modal rework). Dropdowns open
          upward (side="top"). */}
      <SidebarFooter className="border-t border-sidebar-border" data-testid="v4-sidebar-footer">
        <SidebarMenu>
          <SidebarSupport />
          <SidebarMenuItem>
            <UserMenu />
          </SidebarMenuItem>
        </SidebarMenu>
        {/* China ICP registration line — v3 SiderFooter showed it on every page,
            not just login (MIIT display expectations cover the whole site).
            Hidden in the icon rail — the text can't fit a collapsed sidebar. */}
        <div className="group-data-[collapsible=icon]:hidden">
          <IcpText />
        </div>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
};
